import cx from 'classnames';
import {filter, includes, isEmpty, map} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, LoadingDots, UserParticipantIds} from 'components';
import {SessionTextChatMessageOwner} from 'enums';
import {AnyParticipant, Session} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {DefaultParticipant, ParticipantRole, TextChatContentType, TextChatMessage} from 'types';
import {sessionUtils} from 'utils';

import {TextChatMessageItem} from './TextChatMessageItem/TextChatMessageItem';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT;

type Props = {
    messagesEndRef: React.RefObject<HTMLDivElement>;
    localParticipantId: string;
    participants: AnyParticipant[];
    userParticipantIds: UserParticipantIds;
    messages: Partial<TextChatMessage>[];
    downloadingFiles: string[];
    downloadFileDisabled: boolean;
    typingRemoteParticipantIds: string[];
    hideEmptyMessagesLabel?: boolean;
    conversationMode?: boolean;
    downloadFile: (message: Partial<TextChatMessage>) => void;
    getTranslation?: (message: Partial<TextChatMessage>) => string;
}

export const TextChatMessageList: React.FC<Props> = ({
    localParticipantId, messages, downloadingFiles, typingRemoteParticipantIds, messagesEndRef, participants,
    hideEmptyMessagesLabel, downloadFileDisabled, conversationMode, downloadFile, getTranslation
}) => {
    const {t} = useTranslation();
    const showNoMessagesText = useMemo(() => !hideEmptyMessagesLabel &&
        isEmpty(messages) && isEmpty(typingRemoteParticipantIds), [hideEmptyMessagesLabel, messages, typingRemoteParticipantIds]);
    const visibleMessages = useMemo(() => filter(messages, (m) =>
        !!m.content && m.contentType !== TextChatContentType.IdleReminder),
    [messages]);

    const renderIcon = useCallback((msg: Partial<TextChatMessage>) => {
        const senderId = msg?.from?.id;
        const senderRole = sessionUtils.getParticipantById({participants} as any as Session<AnyParticipant>, senderId)?.role;
        if (senderId === DefaultParticipant.Chatbot) {
            return <Icon name='chatbot' size='lg' color='alternative' />;
        } else if (senderId === DefaultParticipant.System) {
            return <Icon name='camera' size='xl' />;
        } else if (senderRole === ParticipantRole.Agent) {
            return <Icon name='agent-participant' color='dark' size='lg' />;
        } else if (senderRole === ParticipantRole.Assistant) {
            return <Icon name='assistant-participant' color='dark' size='md' />;
        } else if (senderRole === ParticipantRole.Guest) {
            return <Icon name='guest-participant' color='dark' size='md' />;
        }

        return <Icon name='client-participant' color='dark' size='md' />;
    }, [participants]);

    const renderTypingIndicator = useCallback((participantIds: string[]) => (
        map(participantIds, (id) => {
            const messageOwner = sessionUtils.getTextChatMessageOwner(id, localParticipantId);
            return (
                <div key={id} className='typing-indicator'>
                    <div className={cx('message', {
                        foreign: messageOwner === SessionTextChatMessageOwner.Foreign,
                        chatbot: messageOwner === SessionTextChatMessageOwner.Chatbot,
                        system: messageOwner === SessionTextChatMessageOwner.System
                    })}
                    >
                        <div className='message-icon'>{renderIcon({from: {id, name: null}})}</div>
                        <div className='message-content'>
                            <LoadingDots dotColor='primary' size='small' />
                        </div>
                    </div>
                </div>
            );
        })
    ), [localParticipantId, renderIcon]);

    return (
        <div className='messages'>
            {showNoMessagesText && <div className='no-messages'>{t(T_KEYS.NO_MESSAGES)}</div>}
            {map(visibleMessages, (message) => (
                <TextChatMessageItem
                    key={message.id}
                    participants={participants}
                    localParticipantId={localParticipantId}
                    message={message}
                    downloadFileDisabled={downloadFileDisabled}
                    isDownloading={includes(downloadingFiles, message.id)}
                    renderIcon={renderIcon}
                    conversationMode={conversationMode}
                    downloadFile={downloadFile}
                    getTranslation={getTranslation}
                />
            ))}
            {renderTypingIndicator(typingRemoteParticipantIds)}
            <div ref={messagesEndRef} />
        </div>
    );
};